<template>
  <div class="payments-section">
    <TopBarBack
      title="결제하기"
      :with-back="false"
      with-close
      @click-close="handleGoBack"
      class="top-bar"
    />

    <div
      v-if="checkoutData"
      class="order-container"
    >
      <InsuranceContent :checkout-data="checkoutData" />
    </div>

    <div
      v-else-if="isLoading"
      class="state-box"
    >
      <p>불러오는 중...</p>
    </div>

    <div
      v-else
      class="state-box"
    >
      <p>결제 정보를 불러오지 못했어요.</p>
      <button
        type="button"
        class="retry-btn"
        @click="handleGoBack"
      >
        다시 가입하기
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import InsuranceContent from '@/uComponents/order/_contents/InsuranceContent.vue'
import { useBFCacheGuard } from '@/uComposables/order/useBFCacheGuard'
import type { InsuranceCheckoutData } from '@/uTypes/order/insurance'

definePageMeta({
  name: 'insurance',
  pageTransition: false,
  layout: 'center',
  meta: [
    {
      name: 'robots',
      content: 'noindex,follow'
    }
  ]
})

const route = useRoute()
const router = useRouter()
const {
  public: { productUrl }
} = useRuntimeConfig()

const { guardBFCacheOrderPage } = useBFCacheGuard()
const { getInsuranceCheckout } = useInsuranceAPI()

const checkoutData = ref<InsuranceCheckoutData | null>(null)
const isLoading = ref(true)

// 가입 요약은 더 이상 쿠키(insurance_payment_view)로 넘어오지 않는다 —
// product-usimsa 가 넘긴 orderGroupId 로 서버 체크아웃 API 를 조회한다.
// 피보험자 실명 등 개인정보를 클라이언트에 저장하지 않기 위함.
const orderGroupId = computed(() =>
  typeof route.query.orderGroupId === 'string' ? route.query.orderGroupId : ''
)

const handleGoBack = () => {
  window.location.href = `${productUrl}/insurance/apply`
}

onMounted(async () => {
  if (guardBFCacheOrderPage()) return

  if (!orderGroupId.value) {
    isLoading.value = false
    return
  }

  try {
    const res = await getInsuranceCheckout(orderGroupId.value)
    const data = 'data' in res ? res.data : null

    if (!data) {
      isLoading.value = false
      return
    }

    if (data.status === 'PAID') {
      // 결제 페이지 재진입(뒤로가기/새로고침) — 이미 승인됐으므로 계약 상세로 보낸다.
      await router.replace(`/user/history/insurance/${data.orderGroupId}`)
      return
    }

    checkoutData.value = data
    useAirbridge().trackInsuranceOrderInfo(data)
  } catch (error) {
    // 410(스테이징 만료/삭제) 포함 — 공통 에러 핸들러가 알럿을 띄우고, 화면은 재가입 유도로 남는다.
    console.error('보험 체크아웃 조회 실패', error)
  } finally {
    isLoading.value = false
  }
})
</script>
<style scoped lang="scss">
.top-bar {
  background-color: $gray-100;
}
.payments-section {
  background-color: $gray-100;
}
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 80px 16px;
  text-align: center;

  .retry-btn {
    padding: 10px 20px;
    border-radius: 8px;
    background-color: $gray-700;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
  }
}
</style>
